import Alert from '@risk-smart/themed-cloudscape-components/alert';
import type { CollectionPreferencesProps } from '@risk-smart/themed-cloudscape-components/collection-preferences';
import Container from '@risk-smart/themed-cloudscape-components/container';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { useGetOptionalGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { type FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import DeleteModal from 'src/components/delete-modal';
import { useSmartCopyAmplitude } from 'src/hooks/smart-copy';
import PageLayout from 'src/layouts/PageLayout';
import { useCustomAttributeLookup } from 'src/pages/dashboards/custom-data-source-widget/useCustomAttributeLookup';
import { Permission } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useDeleteCustomDatasource } from '@/hooks/mutations/custom-datasource/useDeleteCustomDatasource';
import { useInsertCustomDatasource } from '@/hooks/mutations/custom-datasource/useInsertCustomDatasource';
import { useUpdateCustomDatasource } from '@/hooks/mutations/custom-datasource/useUpdateCustomDatasource';
import {
  useGetAllFormsCustomisation,
  useGetCustomDatasourceById,
  useLazyGetReportingData,
} from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';

import {
  CUSTOM_DATASOURCE_SMART_COPY,
  readCopyFromCustomDatasourceId,
} from '../customDatasourceSmartCopy';
import {
  parseOptionalTypedCustomDatasource,
  type TypedCustomDatasource,
} from '../types';
import { buildCopiedCustomDatasourceDefaults } from './buildCopiedCustomDatasourceDefaults';
import { CustomDatasourceForm } from './CustomDatasourceForm';
import type { CustomDatasourceFormData } from './customDatasourceSchema';
import CustomDatasourceTable from './CustomDatasourceTable';
import {
  mapFromDataToServerVariables,
  mapServerDataToFormData,
} from './formDataMapping';
import { DEFAULT_PAGE_SIZE } from './tablePreferences';
import type { RelatedDataSource } from './types';
import { buildColumnWidthScope } from './useCustomDatasourceColumnWidths';

const Page: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const {
    hasPermission: canUpdateDatasource,
    loading: canUpdateDatasourceLoading,
  } = useHasPermissionQuery('update:custom_datasource');
  const customDataSourceId = useGetOptionalGuidParam('customDatasourceId');
  // Opening the create page pre-populated from a selected source: the register
  // passes the source id via nav state. Copy is create-mode only — never when
  // editing an existing record (which owns the route param).
  const copyFromId = readCopyFromCustomDatasourceId(location.state);
  const isCopyMode = !customDataSourceId && Boolean(copyFromId);
  // Unlike the register, this page is reachable with the flag off (via the plain
  // Add button), so the save events need an explicit flag read.
  const isSmartCopyEnabled = useIsFeatureFlagEnabled(
    CUSTOM_DATASOURCE_SMART_COPY.flagKey
  );
  const { events, logEvent } = useSmartCopyAmplitude({
    entityLabel: CUSTOM_DATASOURCE_SMART_COPY.eventLabel,
  });
  const [preferences, setPreferences] =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    useState<CollectionPreferencesProps.Preferences<any>>();

  const { data: customDatasource, error } = useGetCustomDatasourceById({
    queryArgs: { id: customDataSourceId ?? '' },
    shouldSkip: !customDataSourceId,
  });
  const savedCustomDatasource: null | TypedCustomDatasource | undefined =
    useMemo(
      () =>
        parseOptionalTypedCustomDatasource(
          customDatasource?.custom_datasource_by_pk
        ),
      [customDatasource?.custom_datasource_by_pk]
    );

  if (customDataSourceId && error) {
    throw error;
  }
  if (customDataSourceId && customDatasource && !savedCustomDatasource) {
    throw new PageNotFound(
      `Custom datasource with id ${customDataSourceId} not found`
    );
  }

  // Copy mode fetches the source through a SEPARATE query from the edit fetch
  // above, deliberately NOT bound to `savedCustomDatasource`: that variable
  // drives the save handler's update-vs-insert choice, so reusing it here would
  // make a copy overwrite its source instead of creating a new record.
  const {
    data: copySourceData,
    loading: copySourceLoading,
    error: copySourceError,
  } = useGetCustomDatasourceById({
    queryArgs: { id: copyFromId ?? '' },
    shouldSkip: !isCopyMode,
  });
  const copySource: null | TypedCustomDatasource | undefined = useMemo(
    () =>
      parseOptionalTypedCustomDatasource(
        copySourceData?.custom_datasource_by_pk
      ),
    [copySourceData?.custom_datasource_by_pk]
  );

  // Surface a copy-source fetch failure, or a source that is missing / no longer
  // readable, rather than silently opening a blank create form.
  if (isCopyMode && copySourceError) {
    throw copySourceError;
  }
  if (isCopyMode && !copySourceLoading && !copySource) {
    throw new Error(
      `Source custom datasource ${copyFromId} not found for copy`
    );
  }

  const { t } = useTranslation(['common'], { keyPrefix: 'customDatasources' });
  const [definitionFormData, setDefinitionFormData] =
    useState<CustomDatasourceFormData>();
  const { data: formCustomisationData } = useGetAllFormsCustomisation({
    queryArgs: {},
  });
  const { customAttributeSchemaLookup } = useCustomAttributeLookup();
  const [getReportData, getReportDataResult] = useLazyGetReportingData();
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const { insertCustomDatasource } = useInsertCustomDatasource();
  const { updateCustomDatasource } = useUpdateCustomDatasource();
  const { deleteCustomDatasource, loading: deleteLoading } =
    useDeleteCustomDatasource();

  const pageIndex = useRef(0);
  const pageSize = preferences?.pageSize ?? DEFAULT_PAGE_SIZE;

  /**
   * `pageSizeOverride` exists because the report query is lazy: when the user changes
   * the page-size preference we must refetch immediately, before the state update has
   * re-rendered, so the closed-over `pageSize` is still the previous value.
   */
  const fetchData = (
    formData: CustomDatasourceFormData,
    pageSizeOverride?: number
  ) => {
    const effectivePageSize = pageSizeOverride ?? pageSize;

    return getReportData(
      mapFromDataToServerVariables(
        formData,
        {
          offset: pageIndex.current * effectivePageSize,
          limit: effectivePageSize,
        },
        undefined
      )
    );
  };

  const onDelete = useDeleteResultNotification({
    entityName: t('entity_name'),
    asyncAction: async () => {
      if (!savedCustomDatasource) {
        return false;
      }
      await deleteCustomDatasource({
        Id: savedCustomDatasource.Id,
      });
      await navigate('..' + location.hash);

      return true;
    },
  });

  // Previewing feeds the submitted data back in, which is what preserves typed
  // input across the re-render it triggers.
  const values = useMemo<CustomDatasourceFormData | undefined>(() => {
    if (definitionFormData) {
      return definitionFormData;
    }
    if (savedCustomDatasource) {
      return mapServerDataToFormData(savedCustomDatasource);
    }

    return undefined;
  }, [savedCustomDatasource, definitionFormData]);

  const copiedDefaultValues = useMemo<CustomDatasourceFormData | undefined>(
    () =>
      copySource ? buildCopiedCustomDatasourceDefaults(copySource) : undefined,
    [copySource]
  );
  const definition = definitionFormData
    ? mapFromDataToServerVariables(
        definitionFormData,
        {
          offset: pageIndex.current * pageSize,
          limit: pageSize,
        },
        undefined
      )
    : undefined;

  // Object-level toolbar (CUS-172) — flag-gated. This create/edit surface keeps
  // its own form Save / Preview / Cancel controls (no toolbar save disc), so the
  // toolbar only relocates the header Delete action (edit mode only), gated on
  // delete:custom_datasource — parity with the legacy Permission-wrapped button.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: { title: savedCustomDatasource?.Title ?? t('create_title') },
      actions:
        savedCustomDatasource && customDataSourceId
          ? {
              danger: [
                {
                  id: 'delete',
                  label: t('delete_button'),
                  iconName: 'trash-01',
                  permission: 'delete:custom_datasource',
                  // Hydration gate (ENG-616) — the legacy button already has it.
                  disabled: !savedCustomDatasource,
                  onClick: () => setIsDeleteModalVisible(true),
                },
              ],
            }
          : undefined,
    }),
    [savedCustomDatasource, customDataSourceId, t]
  );

  return (
    <PageLayout
      title={savedCustomDatasource?.Title ?? t('create_title')}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        savedCustomDatasource && (
          <SpaceBetween direction={'horizontal'} size={'xxs'}>
            {customDataSourceId && (
              <Permission permission={'delete:custom_datasource'}>
                <Button
                  variant={'normal'}
                  formAction={'none'}
                  // Wait for the entity to hydrate. `onDelete` guards on
                  // `if (!savedCustomDatasource) return false`, which surfaces as
                  // a bare "Failed to save changes" with NO request issued, so a
                  // click inside the hydration window looks like a broken delete
                  // (ENG-616). Disabling also restores Playwright's actionability
                  // wait.
                  disabled={!savedCustomDatasource}
                  onClick={async () => {
                    setIsDeleteModalVisible(true);
                  }}
                >
                  {t('delete_button')}
                </Button>
              </Permission>
            )}
          </SpaceBetween>
        )
      }
    >
      <Container>
        {customAttributeSchemaLookup && !(isCopyMode && copySourceLoading) && (
          <CustomDatasourceForm
            formFieldConfigurations={
              formCustomisationData?.form_field_configuration ?? null
            }
            mode={customDataSourceId ? 'update' : 'create'}
            readOnly={!canUpdateDatasource || canUpdateDatasourceLoading}
            customAttributeSchemaLookup={customAttributeSchemaLookup}
            values={values}
            defaultValues={copiedDefaultValues}
            onPreview={async (formData) => {
              pageIndex.current = 0;
              setDefinitionFormData(formData);
              fetchData(formData);
            }}
            onDismiss={async (saved) => {
              if (!saved) {
                navigate('..' + location.hash);
              }
            }}
            onSave={async (formData) => {
              pageIndex.current = 0;
              setDefinitionFormData(formData);
              const mappedFromData = mapFromDataToServerVariables(
                formData,
                {
                  offset: pageIndex.current * pageSize,
                  limit: pageSize,
                },
                preferences?.contentDisplay?.map((c) => c.id)
              );
              if (savedCustomDatasource) {
                await updateCustomDatasource({
                  Id: savedCustomDatasource.Id,
                  Title: formData.title,
                  Fields: mappedFromData.fields,
                  Datasources: mappedFromData.dataSources,
                  Filters: mappedFromData.filters,
                });
              } else {
                await insertCustomDatasource({
                  Title: formData.title,
                  Fields: mappedFromData.fields,
                  Datasources: mappedFromData.dataSources,
                  Filters: mappedFromData.filters,
                });
                // After the await, so a failed insert throws and fires nothing.
                // The update branch stays silent — an edit is not a create.
                if (isSmartCopyEnabled) {
                  if (copyFromId) {
                    logEvent(events.SMART_COPY_SAVED, {
                      sourceCustomDatasourceId: copyFromId,
                    });
                  } else {
                    logEvent(events.BASELINE_SAVED, {});
                  }
                }
              }
              navigate('..' + location.hash);
            }}
          />
        )}
      </Container>

      {getReportDataResult.error && !getReportDataResult.loading && (
        <Alert type={'error'}>{t('data_request_failure_message')}</Alert>
      )}
      {customAttributeSchemaLookup && definition && (
        <CustomDatasourceTable
          columnWidthScope={
            customDataSourceId
              ? buildColumnWidthScope.editor(customDataSourceId)
              : undefined
          }
          formFieldConfigurations={
            formCustomisationData?.form_field_configuration ?? null
          }
          preferences={preferences}
          onSetPreferences={(updated) => {
            // Compare effective sizes so explicitly picking the already-active
            // default isn't treated as a change (mirrors the `pageSize` derivation
            // above), which would otherwise reset the page and refetch for nothing.
            const nextPageSize = updated.pageSize ?? DEFAULT_PAGE_SIZE;
            const pageSizeChanged = nextPageSize !== pageSize;
            setPreferences(updated);
            // Page size drives the server-side limit/offset and the report query is
            // lazy, so reset to the first page and refetch explicitly.
            if (pageSizeChanged) {
              pageIndex.current = 0;
              if (definitionFormData) {
                fetchData(definitionFormData, nextPageSize);
              }
            }
          }}
          columnsAlwaysVisible={true}
          customAttributeSchemaLookup={customAttributeSchemaLookup}
          loading={getReportDataResult.loading}
          pageSize={pageSize}
          currentPageIndex={pageIndex.current}
          onPageChangeClick={({ requestedPageIndex }) => {
            pageIndex.current = requestedPageIndex;
            if (!definitionFormData) {
              throw new Error('Missing form data');
            }
            fetchData(definitionFormData);
          }}
          customDatasource={{
            Fields: definition.fields,
            Datasources: definition.dataSources as RelatedDataSource[],
          }}
          items={getReportDataResult.data?.reportingData ?? []}
        />
      )}
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={t('delete_modal_title')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {t('confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
